import React, { useState, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../contexts/SubscriptionContext';
import {
  Upload,
  FileText,
  X,
  Check,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Edit2,
  Trash2,
  Download,
  CheckCircle,
  XCircle,
  Lock,
  Crown
} from 'lucide-react';

// Get backend URL
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const API = `${getBackendUrl()}/api`;

// Category options for editing
const CATEGORY_OPTIONS = [
  { value: 'food', label: { en: 'Food', es: 'Comida' } },
  { value: 'transport', label: { en: 'Transport', es: 'Transporte' } },
  { value: 'shopping', label: { en: 'Shopping', es: 'Compras' } },
  { value: 'entertainment', label: { en: 'Entertainment', es: 'Entretenimiento' } },
  { value: 'groceries', label: { en: 'Groceries', es: 'Supermercado' } },
  { value: 'travel', label: { en: 'Travel', es: 'Viajes' } },
  { value: 'healthcare', label: { en: 'Healthcare', es: 'Salud' } },
  { value: 'utilities', label: { en: 'Utilities', es: 'Servicios' } },
  { value: 'other', label: { en: 'Other', es: 'Otros' } }
];

// Spending type options
const SPENDING_TYPE_OPTIONS = [
  { value: 'basic', label: { en: 'Essential', es: 'Esencial' } },
  { value: 'regular', label: { en: 'Lifestyle', es: 'Estilo de vida' } },
  { value: 'luxury', label: { en: 'Luxury', es: 'Lujo' } }
];

// Steps in the import flow
const STEPS = {
  UPLOAD: 'upload',
  PROCESSING: 'processing',
  REVIEW: 'review',
  IMPORTING: 'importing',
  SUCCESS: 'success',
  ERROR: 'error'
};

const StatementImportModal = ({ isOpen, onClose, onImportComplete, token }) => {
  const { t, i18n } = useTranslation();
  const { isPremium } = useSubscription();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState(null);
  const [importedCount, setImportedCount] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);
  
  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    const allowedTypes = ['application/pdf', 'text/csv', 'application/vnd.ms-excel', 
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const allowedExtensions = ['.pdf', '.csv', '.xlsx', '.xls'];
    
    const fileExtension = droppedFile?.name?.toLowerCase().slice(droppedFile.name.lastIndexOf('.'));
    
    if (droppedFile && (allowedTypes.includes(droppedFile.type) || allowedExtensions.includes(fileExtension))) {
      setFile(droppedFile);
    } else {
      toast.error(lang === 'es' 
        ? 'Formatos permitidos: PDF, CSV, Excel (.xlsx, .xls)' 
        : 'Allowed formats: PDF, CSV, Excel (.xlsx, .xls)');
    }
  }, [lang]);
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    const allowedExtensions = ['.pdf', '.csv', '.xlsx', '.xls'];
    const fileExtension = selectedFile?.name?.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (selectedFile && allowedExtensions.includes(fileExtension)) {
      setFile(selectedFile);
    } else {
      toast.error(lang === 'es' 
        ? 'Formatos permitidos: PDF, CSV, Excel (.xlsx, .xls)' 
        : 'Allowed formats: PDF, CSV, Excel (.xlsx, .xls)');
    }
  };
  
  // Upload and parse the statement
  const handleUpload = async () => {
    if (!file) return;
    
    // Check file size - warn for large files
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 5) {
      toast.warning(lang === 'es'
        ? `Archivo grande (${fileSizeMB.toFixed(1)}MB). El procesamiento puede tomar más tiempo.`
        : `Large file (${fileSizeMB.toFixed(1)}MB). Processing may take longer.`);
    }
    
    setStep(STEPS.PROCESSING);
    setError(null);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use XMLHttpRequest for upload progress tracking
      const uploadWithProgress = () => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 100);
              setUploadProgress(progress);
            }
          });
          
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                resolve({ ok: true, status: xhr.status, data });
              } catch (e) {
                reject(new Error('Invalid response format'));
              }
            } else if (xhr.status === 403) {
              resolve({ ok: false, status: 403, data: null });
            } else {
              try {
                const errorData = JSON.parse(xhr.responseText);
                reject(new Error(errorData.detail || 'Upload failed'));
              } catch (e) {
                reject(new Error('Upload failed'));
              }
            }
          });
          
          xhr.addEventListener('error', () => {
            reject(new Error('Network error - check your connection'));
          });
          
          xhr.addEventListener('timeout', () => {
            reject(new Error('Upload timeout - file may be too large'));
          });
          
          xhr.open('POST', `${API}/statement-import/upload`);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.timeout = 120000; // 2 minute timeout
          xhr.send(formData);
        });
      };
      
      const result = await uploadWithProgress();
      
      if (result.status === 403) {
        setStep(STEPS.ERROR);
        setError(lang === 'es' 
          ? 'Importar estados de cuenta es una función Premium. Por favor actualiza tu cuenta.'
          : 'Statement import is a Premium feature. Please upgrade to access.');
        return;
      }
      
      const data = result.data;
      
      // If processing in background (PDFs with OCR), poll for status
      if (data.status === 'processing') {
        setBatchId(data.batch_id);
        setUploadProgress(100);
        pollForResults(data.batch_id);
        return;
      }
      
      if (data.transaction_count === 0) {
        setStep(STEPS.ERROR);
        setError(lang === 'es' 
          ? 'No pudimos detectar transacciones en este archivo. Intenta con PDF, CSV o Excel.'
          : 'We could not detect transactions in this file. Try PDF, CSV, or Excel.');
        return;
      }
      
      setBatchId(data.batch_id);
      setCurrency(data.currency || 'USD');
      setTransactions(data.transactions.map(t => ({
        ...t,
        description: t.cleaned_description || t.description
      })));
      setStep(STEPS.REVIEW);
      
    } catch (err) {
      console.error('Upload error:', err);
      setStep(STEPS.ERROR);
      
      if (err.message.includes('timeout')) {
        setError(lang === 'es' 
          ? 'La carga tardó demasiado. Intenta con un archivo más pequeño o en formato CSV/Excel.'
          : 'Upload took too long. Try a smaller file or CSV/Excel format.');
      } else if (err.message.includes('Network') || err.message.includes('network')) {
        setError(lang === 'es' 
          ? 'Error de conexión. Verifica tu internet e intenta de nuevo.'
          : 'Connection error. Check your internet and try again.');
      } else {
        setError(err.message || (lang === 'es' 
          ? 'Error al procesar el archivo'
          : 'Error processing the file'));
      }
    }
  };
  
  // Poll for batch status (for PDFs processed in background)
  const pollForResults = async (pollBatchId) => {
    const maxAttempts = 90; // 3 minutes max (OCR on large PDFs can take time)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await fetch(`${API}/statement-import/batches/${pollBatchId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to get batch status');
        }
        
        const data = await response.json();
        
        if (data.status === 'processing') {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000); // Poll every 2 seconds
          } else {
            setStep(STEPS.ERROR);
            setError(lang === 'es'
              ? 'El procesamiento tardó demasiado. Intenta de nuevo o usa formato CSV/Excel.'
              : 'Processing took too long. Try again or use CSV/Excel format.');
          }
          return;
        }
        
        if (data.status === 'failed' || !data.success) {
          setStep(STEPS.ERROR);
          setError(data.error || (lang === 'es' 
            ? 'Error al procesar el archivo'
            : 'Error processing the file'));
          return;
        }
        
        if (data.transaction_count === 0) {
          setStep(STEPS.ERROR);
          setError(lang === 'es' 
            ? 'No pudimos detectar transacciones en este archivo.'
            : 'We could not detect transactions in this file.');
          return;
        }
        
        setCurrency(data.currency || 'USD');
        setTransactions(data.transactions.map(t => ({
          ...t,
          description: t.cleaned_description || t.description
        })));
        setStep(STEPS.REVIEW);
        
      } catch (err) {
        console.error('Poll error:', err);
        setStep(STEPS.ERROR);
        setError(lang === 'es' 
          ? 'Error al obtener el estado del procesamiento'
          : 'Error getting processing status');
      }
    };
    
    poll();
  };
  
  // Toggle transaction selection
  const toggleTransaction = (id) => {
    setTransactions(prev => prev.map(t => 
      t.id === id ? { ...t, is_selected: !t.is_selected } : t
    ));
  };
  
  // Select/deselect all
  const toggleAll = (selected) => {
    setTransactions(prev => prev.map(t => ({ ...t, is_selected: selected })));
  };
  
  // Update transaction field
  const updateTransaction = (id, field, value) => {
    setTransactions(prev => prev.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };
  
  // Import selected transactions
  const handleImport = async () => {
    const selectedCount = transactions.filter(t => t.is_selected).length;
    if (selectedCount === 0) {
      toast.error(lang === 'es' 
        ? 'Por favor selecciona al menos una transacción'
        : 'Please select at least one transaction');
      return;
    }
    
    setStep(STEPS.IMPORTING);
    
    try {
      // First update any edited transactions
      for (const trans of transactions) {
        if (trans.is_selected) {
          await fetch(`${API}/statement-import/transactions/${trans.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              cleaned_description: trans.description,
              category: trans.category,
              spending_type: trans.spending_type,
              is_selected: trans.is_selected,
              amount: trans.amount
            })
          });
        }
      }
      
      // Then import
      const response = await fetch(`${API}/statement-import/batches/${batchId}/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to import transactions');
      }
      
      const data = await response.json();
      setImportedCount(data.imported_count);
      setStep(STEPS.SUCCESS);
      
      // Notify parent to refresh expenses
      if (onImportComplete) {
        onImportComplete(data.imported_count);
      }
      
    } catch (err) {
      console.error('Import error:', err);
      setStep(STEPS.ERROR);
      setError(lang === 'es' 
        ? 'Error al importar las transacciones'
        : 'Error importing transactions');
    }
  };
  
  // Reset and close
  const handleClose = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setTransactions([]);
    setBatchId(null);
    setError(null);
    onClose();
  };
  
  // Format date for display
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  
  // Format currency
  const formatAmount = (amount, curr = currency) => {
    return new Intl.NumberFormat(lang === 'es' ? 'es-MX' : 'en-US', {
      style: 'currency',
      currency: curr
    }).format(amount);
  };
  
  if (!isOpen) return null;
  
  // Premium paywall
  if (!isPremium) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md bg-white">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-yellow-600" />
            </div>
            <CardTitle className="text-xl">
              {lang === 'es' ? 'Función Premium' : 'Premium Feature'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              {lang === 'es' 
                ? 'Importar estados de cuenta es una función Premium. Actualiza tu cuenta para acceder a esta y otras funciones exclusivas.'
                : 'Import bank statements is a Premium feature. Upgrade your account to access this and other exclusive features.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={handleClose}>
                {lang === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black">
                <Crown className="w-4 h-4 mr-2" />
                {lang === 'es' ? 'Actualizar a Premium' : 'Upgrade to Premium'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white flex flex-col">
        {/* Header */}
        <CardHeader className="border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {lang === 'es' ? 'Importar Estado de Cuenta' : 'Import Statement'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-auto p-6">
          {/* Step: Upload */}
          {step === STEPS.UPLOAD && (
            <div className="space-y-6">
              <div className="text-center max-w-md mx-auto">
                <h2 className="text-xl font-semibold mb-2">
                  {lang === 'es' ? 'Importa tu estado de cuenta' : 'Import your bank statement'}
                </h2>
                <p className="text-gray-600 text-sm">
                  {lang === 'es' 
                    ? 'Sube un archivo PDF, CSV o Excel de tu estado de cuenta bancario o de tarjeta de crédito. ProsperMind detectará automáticamente tus transacciones.'
                    : 'Upload a PDF, CSV or Excel bank or credit card statement. ProsperMind will automatically detect your transactions.'}
                </p>
              </div>
              
              {/* Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-50' 
                    : file 
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {file ? (
                  <div className="space-y-3">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                    <p className="font-medium text-green-700">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setFile(null)}
                    >
                      <X className="w-4 h-4 mr-1" />
                      {lang === 'es' ? 'Quitar' : 'Remove'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <p className="text-gray-600">
                      {lang === 'es' 
                        ? 'Arrastra y suelta tu archivo aquí'
                        : 'Drag and drop your file here'}
                    </p>
                    <p className="text-xs text-gray-400">
                      PDF, CSV, Excel (.xlsx, .xls)
                    </p>
                    <p className="text-sm text-gray-400">
                      {lang === 'es' ? 'o' : 'or'}
                    </p>
                    <Button 
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {lang === 'es' ? 'Seleccionar Archivo' : 'Select File'}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
              
              {/* Privacy note */}
              <p className="text-xs text-center text-gray-500">
                🔒 {lang === 'es' 
                  ? 'Tus datos financieros están encriptados y nunca se comparten.'
                  : 'Your financial data is encrypted and never shared.'}
              </p>
              
              {/* Upload button */}
              {file && (
                <div className="flex justify-center">
                  <Button 
                    onClick={handleUpload}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {lang === 'es' ? 'Procesar PDF' : 'Process PDF'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Step: Processing */}
          {step === STEPS.PROCESSING && (
            <div className="text-center py-12 space-y-4">
              <Loader2 className="w-16 h-16 text-blue-600 mx-auto animate-spin" />
              <h2 className="text-xl font-semibold">
                {uploadProgress < 100 
                  ? (lang === 'es' ? 'Subiendo archivo...' : 'Uploading file...')
                  : (lang === 'es' ? 'Leyendo tu estado de cuenta' : 'Reading your statement')}
              </h2>
              
              {/* Upload Progress Bar */}
              {uploadProgress < 100 && (
                <div className="w-64 mx-auto">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {uploadProgress}%
                  </p>
                </div>
              )}
              
              {uploadProgress >= 100 && (
                <p className="text-gray-600">
                  {lang === 'es' 
                    ? 'Extrayendo transacciones de tu documento...'
                    : 'Extracting transactions from your document...'}
                </p>
              )}
              
              <p className="text-sm text-amber-600">
                {lang === 'es' 
                  ? 'Los PDFs con imágenes pueden tardar hasta 30 segundos'
                  : 'Image-based PDFs may take up to 30 seconds'}
              </p>
            </div>
          )}
          
          {/* Step: Review */}
          {step === STEPS.REVIEW && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">
                    {lang === 'es' ? 'Revisa las transacciones detectadas' : 'Review detected transactions'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {transactions.length} {lang === 'es' ? 'transacciones encontradas' : 'transactions found'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleAll(true)}
                  >
                    {lang === 'es' ? 'Seleccionar todo' : 'Select all'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toggleAll(false)}
                  >
                    {lang === 'es' ? 'Deseleccionar todo' : 'Deselect all'}
                  </Button>
                </div>
              </div>
              
              {/* Transactions table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-3 text-left w-10"></th>
                        <th className="p-3 text-left">{lang === 'es' ? 'Fecha' : 'Date'}</th>
                        <th className="p-3 text-left">{lang === 'es' ? 'Descripción' : 'Description'}</th>
                        <th className="p-3 text-right">{lang === 'es' ? 'Monto' : 'Amount'}</th>
                        <th className="p-3 text-left">{lang === 'es' ? 'Categoría' : 'Category'}</th>
                        <th className="p-3 text-left">{lang === 'es' ? 'Tipo' : 'Type'}</th>
                        <th className="p-3 text-center w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((trans) => (
                        <tr 
                          key={trans.id}
                          className={`border-t ${trans.is_duplicate ? 'bg-yellow-50' : ''} ${!trans.is_selected ? 'opacity-50' : ''}`}
                        >
                          <td className="p-3">
                            <Checkbox
                              checked={trans.is_selected}
                              onCheckedChange={() => toggleTransaction(trans.id)}
                            />
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            {formatDate(trans.date)}
                          </td>
                          <td className="p-3">
                            {editingId === trans.id ? (
                              <Input
                                value={trans.description}
                                onChange={(e) => updateTransaction(trans.id, 'description', e.target.value)}
                                className="h-8"
                              />
                            ) : (
                              <div>
                                <span>{trans.description}</span>
                                {trans.is_duplicate && (
                                  <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">
                                    {lang === 'es' ? 'Posible duplicado' : 'Possible duplicate'}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right whitespace-nowrap font-medium">
                            {editingId === trans.id ? (
                              <Input
                                type="number"
                                value={trans.amount}
                                onChange={(e) => updateTransaction(trans.id, 'amount', parseFloat(e.target.value))}
                                className="h-8 w-24"
                              />
                            ) : (
                              formatAmount(trans.amount)
                            )}
                          </td>
                          <td className="p-3">
                            {editingId === trans.id ? (
                              <Select
                                value={trans.category}
                                onValueChange={(v) => updateTransaction(trans.id, 'category', v)}
                              >
                                <SelectTrigger className="h-8 w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CATEGORY_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label[lang]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="capitalize">
                                {CATEGORY_OPTIONS.find(c => c.value === trans.category)?.label[lang] || trans.category}
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            {editingId === trans.id ? (
                              <Select
                                value={trans.spending_type}
                                onValueChange={(v) => updateTransaction(trans.id, 'spending_type', v)}
                              >
                                <SelectTrigger className="h-8 w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {SPENDING_TYPE_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label[lang]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="capitalize">
                                {SPENDING_TYPE_OPTIONS.find(s => s.value === trans.spending_type)?.label[lang] || trans.spending_type}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingId(editingId === trans.id ? null : trans.id)}
                            >
                              {editingId === trans.id ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Edit2 className="w-4 h-4" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Summary and import button */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  {transactions.filter(t => t.is_selected).length} {lang === 'es' ? 'seleccionadas' : 'selected'} • 
                  <span className="font-medium ml-1">
                    {formatAmount(transactions.filter(t => t.is_selected).reduce((sum, t) => sum + t.amount, 0))}
                  </span>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleClose}>
                    {lang === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button 
                    onClick={handleImport}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={transactions.filter(t => t.is_selected).length === 0}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {lang === 'es' ? 'Importar Gastos Seleccionados' : 'Import Selected Expenses'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Step: Importing */}
          {step === STEPS.IMPORTING && (
            <div className="text-center py-12 space-y-4">
              <Loader2 className="w-16 h-16 text-green-600 mx-auto animate-spin" />
              <h2 className="text-xl font-semibold">
                {lang === 'es' ? 'Importando gastos...' : 'Importing expenses...'}
              </h2>
            </div>
          )}
          
          {/* Step: Success */}
          {step === STEPS.SUCCESS && (
            <div className="text-center py-12 space-y-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="text-xl font-semibold text-green-700">
                {lang === 'es' ? 'Gastos importados correctamente' : 'Expenses imported successfully'}
              </h2>
              <p className="text-gray-600">
                {lang === 'es' 
                  ? `Se agregaron ${importedCount} gastos.`
                  : `${importedCount} expenses were added.`}
              </p>
              <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700">
                {lang === 'es' ? 'Cerrar' : 'Close'}
              </Button>
            </div>
          )}
          
          {/* Step: Error */}
          {step === STEPS.ERROR && (
            <div className="text-center py-12 space-y-4">
              <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h2 className="text-xl font-semibold text-red-700">
                {lang === 'es' ? 'Error' : 'Error'}
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={handleClose}>
                  {lang === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={() => { setStep(STEPS.UPLOAD); setError(null); }}>
                  {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatementImportModal;
